// audio/index.js: M5 Change #3 entry point.
//
// One audio engine per page. Constructed lazily on first getEngine()
// call so module load doesn't poke AudioContext before window exists.
// Sample registration also goes through here so call sites pick up the
// singleton without each hooking up their own engine.

import { createAudioEngine } from './engine.js';
import { wireAudioEngine } from './wire-engine.js';
import { loadSettings } from '../settings.js';

/** @type {ReturnType<typeof createAudioEngine>|null} */
let _engine = null;
let _wired = false;

// Issue #6: no sound assets ship yet, the `/audio/*.wav` URLs in SAMPLES are
// not in the repo (client/public/audio/ doesn't exist). Loading them on boot
// 404s ("[audio] sample failed to load") and, because the SPA fallback serves
// index.html for the missing files, decodeAudioData chokes on HTML bytes
// ("unable to decode audio data"). Per the silence-only decision we run the
// engine OFF until real samples land: tier 'off' skips every fetch/decode AND
// makes play() a no-op before it can warn. Flip to true when assets ship
// (the SAMPLES manifest below is kept so it stays in lock-step with AUDIO_MAP).
export const AUDIO_ASSETS_AVAILABLE = false;

// M5 #4 + #9 sample manifest. Each entry registers on engine creation.
// Files that don't ship yet (M5 #9 ships most) silently fail to load;
// engine.play() returns false and the game keeps going.
//
// M5 #9: exported so audio-events.test.js can cross-check that every
// sample dispatched by AUDIO_MAP is registered here. Drift in either
// direction is a test failure.
/** @type {Array<[string, string, { vital: boolean }]>} */
export const SAMPLES = [
  ['card_snap',       '/audio/card_snap.wav',       { vital: true  }], // #4
  ['card_dealt',      '/audio/card_dealt.wav',      { vital: false }], // #9
  ['card_hover',      '/audio/card_hover.wav',      { vital: false }], // #9
  ['trump_called',    '/audio/trump_called.wav',    { vital: true  }], // #9
  ['trick_won_us',    '/audio/trick_won_us.wav',    { vital: true  }], // #9
  ['trick_won_them',  '/audio/trick_won_them.wav',  { vital: true  }], // #9
  ['bower_played',    '/audio/bower_played.wav',    { vital: false }], // #9
  ['illegal_play',    '/audio/illegal_play.wav',    { vital: false }], // #6/#9
  ['hand_won_1',      '/audio/hand_won.wav',        { vital: true  }], // #9
  ['hand_won_2',      '/audio/march.wav',           { vital: true  }], // #9
  ['euchred_us',      '/audio/euchred.wav',         { vital: true  }], // #9
  ['euchred_them',    '/audio/euchred_them.wav',    { vital: true  }], // #9
  ['loner_sweep',     '/audio/loner_sweep.wav',     { vital: true  }], // #15
  ['score_tick',      '/audio/score_tick.wav',      { vital: false }], // #11
];

export function getAudioEngine() {
  if (_engine) return _engine;
  const settings = loadSettings();
  // #6: force OFF until sound assets exist, keeps boot free of 404/decode
  // noise. register() at tier 'off' adds samples without fetching them.
  const tier = AUDIO_ASSETS_AVAILABLE ? settings.sound_tier : 'off';
  _engine = createAudioEngine({ tier });
  for (const [id, url, meta] of SAMPLES) _engine.register(id, url, meta);
  if (!_wired) {
    wireAudioEngine(_engine);
    _wired = true;
  }
  return _engine;
}

/**
 * Tier setter that also stays in sync with the audio engine. The
 * settings hook calls this on tier changes.
 *
 * @param {'all'|'vital'|'off'} tier
 */
export function setAudioTier(tier) {
  if (!_engine) return;
  // #6: stay silent until sound assets ship, regardless of the saved setting,
  // so the settings hook can't turn loading back on and re-introduce the
  // missing-sample errors.
  if (!AUDIO_ASSETS_AVAILABLE) return;
  _engine.setTier(tier);
}

/**
 * One-time unlock from a user gesture. Attached to a window pointerdown
 * listener so iOS lifts the AudioContext suspend.
 */
export function installAudioUnlockListener(target) {
  // #6: with no sound assets, don't even create an AudioContext on first
  // gesture, there's nothing to unlock, and a stray context is wasteful.
  if (!AUDIO_ASSETS_AVAILABLE) return () => {};
  const t = target || (typeof window !== 'undefined' ? window : null);
  if (!t) return () => {};
  const onGesture = () => {
    const e = getAudioEngine();
    if (e) e.unlock();
  };
  t.addEventListener('pointerdown', onGesture, { once: true });
  t.addEventListener('keydown',     onGesture, { once: true });
  return () => {
    t.removeEventListener('pointerdown', onGesture);
    t.removeEventListener('keydown',     onGesture);
  };
}

// Test-only reset hook, clears the singleton so re-imports rebuild
// the engine. Not exported in production paths.
export function _resetForTests() {
  if (_engine) {
    try { _engine.dispose(); } catch { /* ignore */ }
  }
  _engine = null;
  _wired = false;
}
